import type { StaticEntityType } from "@config";
import { useIsEditorContext } from "@editor-providers";
import { cn } from "@lib/cn";
import { useIsMobile } from "@library/shared";
import {
  useEffect,
  useMemo,
  useRef,
  type ComponentType,
  type CSSProperties,
} from "react";
import { TableCtxProvider } from "../ctx";
import { useTableSortFilter } from "../hooks/use-table-sort-filter";
import "../styles/table.scss";
import {
  TableHeaderPosition,
  type ITableCellDropTargetProps,
  type ITableCellStackProps,
  type ITableEditorData,
} from "../types";
import { isAllowedInCell } from "../constants/allowed-in-cell-elements";
import { ensureTableElementSettings } from "../utils/ensure-table-element-settings";
import { getMobileColWidths } from "../utils/mobile-col-widths";
import { shouldRenderMobileCards } from "../utils/should-render-mobile-cards";
import { TableCards } from "./table-cards";
import { TableRows } from "./table-rows";
import { TableSortColumnPicker } from "./table-sort-column-picker";

// Published pages carry the full story JSON as `window.story` (see rendering-provider templates).
// Mirrors engagement-bar.js: it bails on `?engagementBar=false`, needs a preset
// URL to fetch before it reveals the bar, and treats only an explicit `false`
// as off -- an absent `active` still renders.
const isEngagementBarActive = (): boolean => {
  if (typeof window === "undefined") return false;
  if (window.location.search.includes("engagementBar=false")) return false;
  const bar = (
    window as {
      story?: {
        settings?: {
          engagementBar?: { presetURL?: string; active?: boolean };
        };
      };
    }
  ).story?.settings?.engagementBar;
  return !!bar?.presetURL && bar.active !== false;
};

interface IProps {
  entity: StaticEntityType;
  editorData?: ITableEditorData;
  resolveCellContent?: (content: string) => string;
  // Editor-injected live in-cell element stack renderer (tables 2.0). Passed
  // outside editorData so inactive tables render their stacks too.
  InCellStack?: ComponentType<ITableCellStackProps>;
  // Like InCellStack: outside editorData so inactive tables stay droppable.
  CellDropTarget?: ComponentType<ITableCellDropTargetProps>;
  // Like InCellStack: outside editorData, because editorData is undefined on an
  // inactive table and a cell must still know its stack renders.
  cellChildIds?: Record<string, string[]>;
}

export const TableView = ({
  entity,
  editorData,
  resolveCellContent,
  InCellStack,
  CellDropTarget,
  cellChildIds,
}: IProps) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const isMobile = useIsMobile();
  const isEditor = useIsEditorContext();
  const elementSettings = useMemo(() => {
    return ensureTableElementSettings(entity.settings);
  }, [entity.settings]);

  const width = editorData?.width || elementSettings.width;

  // Sticky rows stop at the top of the page scrollport, which the fixed
  // Document header covers. Flag the table when the bar is on so table.scss can
  // offset them; the editor has no bar.
  const hasEngagementBar = !isEditor && isEngagementBarActive();

  const mobileColWidths = useMemo(
    () =>
      isMobile
        ? getMobileColWidths(
            elementSettings.tableData,
            elementSettings.tableCols,
            elementSettings.tableRows,
          )
        : [],
    [
      isMobile,
      elementSettings.tableData,
      elementSettings.tableCols,
      elementSettings.tableRows,
    ],
  );

  const {
    sortGated,
    filterGated,
    effectiveSortState,
    effectiveFilterState,
    headerMenuCol,
    openFilterCol,
    rowOrder,
    columnKindByCol,
    uniqueValuesByCol,
    handleHeaderTriggerClick,
    handleSortApply,
    handleFilterTriggerClick,
    handleFilterChange,
  } = useTableSortFilter({ elementSettings, isEditor, containerRef });

  useEffect(() => {
    const overflowEl = containerRef.current;
    if (!overflowEl || !isMobile || !elementSettings.tableHasHeader) return;
    if (elementSettings.tableHeaderPosition === TableHeaderPosition.Right) {
      overflowEl.scrollLeft = overflowEl.scrollWidth;
    }
  }, [
    isMobile,
    elementSettings.tableHasHeader,
    elementSettings.tableHeaderPosition,
  ]);

  // Stabilize the context value so `useContext` consumers only re-render when
  // one of these fields actually changes reference. Before this, ctxState was
  // rebuilt inline on every TableView render, forcing every cell (and every
  // cell-style hook) to re-run on any parent render — the dominant cost when
  // clicking between cells on large tables.
  // Static in-cell element render path: group the table entity's nested
  // children by their cellId (published/preview JSON). Editor contexts render
  // live children through editorData.InCellStack instead.
  const cellChildren = useMemo(() => {
    const children = entity.children;
    if (!children?.length) return undefined;
    const byCell: Record<string, StaticEntityType[]> = {};
    children.forEach((child) => {
      const cellId = (child.settings as { cellId?: string })?.cellId;
      if (!cellId || !isAllowedInCell(child.settings.name as string)) return;
      (byCell[cellId] ??= []).push(child);
    });
    return Object.keys(byCell).length ? byCell : undefined;
  }, [entity.children]);

  const ctxState = useMemo(
    () => ({
      entityId: entity.id,
      editorData,
      elementSettings,
      cellChildren,
      cellChildIds,
      InCellStack,
      CellDropTarget,
      isMobile,
      rowOrder,
      sortState: effectiveSortState,
      headerMenuCol: sortGated ? headerMenuCol : null,
      onHeaderTriggerClick: sortGated ? handleHeaderTriggerClick : undefined,
      onSortApply: sortGated ? handleSortApply : undefined,
      filterState: effectiveFilterState,
      openFilterCol: filterGated ? openFilterCol : null,
      columnKindByCol: filterGated ? columnKindByCol : undefined,
      onFilterTriggerClick: filterGated ? handleFilterTriggerClick : undefined,
      onFilterChange: filterGated ? handleFilterChange : undefined,
      resolveCellContent,
    }),
    [
      entity.id,
      editorData,
      elementSettings,
      cellChildren,
      cellChildIds,
      InCellStack,
      CellDropTarget,
      isMobile,
      rowOrder,
      sortGated,
      effectiveSortState,
      headerMenuCol,
      handleHeaderTriggerClick,
      handleSortApply,
      filterGated,
      effectiveFilterState,
      openFilterCol,
      columnKindByCol,
      handleFilterTriggerClick,
      handleFilterChange,
      resolveCellContent,
    ],
  );

  // Screen-reader announcement for selection/activation changes. The live
  // region was previously empty — keeping it but populating it so screen
  // readers actually get told what changed.
  const liveAnnouncement = useMemo(() => {
    if (!editorData) return "";
    const count = editorData.activeCellsIds?.length ?? 0;
    if (count === 0) return "";
    if (count === 1) return "1 cell selected";
    return `${count} cells selected`;
  }, [editorData]);

  if (
    shouldRenderMobileCards({
      isMobile,
      hasEditorData: !!editorData,
      tableMobileLayout: elementSettings.tableMobileLayout,
    })
  ) {
    return (
      <TableCards
        entity={entity}
        elementSettings={elementSettings}
        resolveCellContent={resolveCellContent}
        InCellStack={InCellStack}
      />
    );
  }

  return (
    <TableCtxProvider ctxState={ctxState}>
      <div
        className={cn("table-scroll-viewport tw-relative tw-flex", {
          "has-engagement-bar": hasEngagementBar,
          "tw-overflow-x-auto": isMobile,
          "table-mobile-scroll":
            isMobile && (elementSettings.tableFrozenRowsCount ?? 0) > 0,
        })}
        data-is-cell-selected={editorData?.activeCellId != null}
        style={
          {
            justifyContent: isMobile
              ? "start"
              : elementSettings.horizontalAlignment,
            "--round": elementSettings.round,
          } as CSSProperties
        }
        ref={(r) => {
          containerRef.current = r;
          const containerEditorRef = editorData?.containerRef;
          if (containerEditorRef) {
            containerEditorRef.current = r;
          }
        }}
      >
        {editorData?.resizeSlot}
        {editorData?.isTableActive && (
          <div className="table-outline-pressable tw-absolute" />
        )}

        <div
          key={entity.id}
          className="table-wrapper tw-relative tw-flex tw-min-w-min tw-flex-col"
          ref={(ref) => {
            const tableWrapperEditorRef = editorData?.tableWrapperRef;
            if (tableWrapperEditorRef) {
              tableWrapperEditorRef.current = ref;
            }
          }}
          style={{
            width: `${isMobile ? 100 : width}%`,
          }}
        >
          {editorData?.selection && editorData?.tableSelectBtnSlot}
          {editorData?.isTableActive && editorData?.cellSelectionSlot}

          {(sortGated || filterGated) && (
            <TableSortColumnPicker
              tableCols={elementSettings.tableCols}
              tableColWidths={elementSettings.tableColWidths}
              mobileColWidths={mobileColWidths}
              sortState={effectiveSortState}
              onSortApply={handleSortApply}
              sortEnabled={sortGated}
              filterEnabled={filterGated}
              filterState={effectiveFilterState}
              uniqueValuesByCol={uniqueValuesByCol}
              columnKindByCol={columnKindByCol}
              onFilterChange={handleFilterChange}
            />
          )}

          <table
            role="grid"
            aria-rowcount={elementSettings.tableRows}
            aria-colcount={elementSettings.tableCols}
            aria-label={elementSettings.name || "Data table"}
            aria-multiselectable={editorData ? true : undefined}
            className="tw-size-full tw-table-fixed"
            style={{ borderCollapse: "separate", borderSpacing: 0 }}
          >
            <TableRows />
          </table>
          {editorData?.dragIndicatorSlot}
        </div>

        {editorData && (
          <div
            role="status"
            aria-live="polite"
            className="tw-absolute tw-h-px tw-w-px tw-overflow-hidden"
            style={{ clipPath: "inset(50%)", whiteSpace: "nowrap" }}
          >
            {liveAnnouncement}
          </div>
        )}
      </div>
    </TableCtxProvider>
  );
};
